// Helpers for walking the nested menu tree built by menuData.js.
// Route params arrive as strings; ids in the tree are numbers.

/**
 * The menu item a route points at, or null if any level is missing.
 * params: { categoryId, subcategoryId?, subsubcategoryId? } from useParams().
 */
export const findItem = (data, { categoryId, subcategoryId, subsubcategoryId }) => {
  const byId = (items, id) => items?.find(item => item.id === parseInt(id, 10)) ?? null;

  const category = byId(data.categories, categoryId);
  if (!category || !subcategoryId) return category;

  const subcategory = byId(category.subcategories, subcategoryId);
  if (!subcategory || !subsubcategoryId) return subcategory;

  return byId(subcategory.subcategories, subsubcategoryId);
};

/** Where a content page's Back button goes: one level up the menu. */
export const backLinkFor = ({ categoryId, subcategoryId, subsubcategoryId }) => {
  if (subsubcategoryId) return `/category/${categoryId}/subcategory/${subcategoryId}`;
  if (subcategoryId) return `/category/${categoryId}`;
  return '/';
};

/**
 * A menu link is either a URL or a per-facility object like
 * { default: '...', otterbein: '...' }. facilityKey is the query param the
 * session started with (see App.js), e.g. 'otterbein'.
 */
export const resolveLink = (link, facilityKey) => {
  const url = link && typeof link === 'object' ? link[facilityKey] || link.default : link;
  // Only web links and same-site paths (the games, /arcade/...) may become an
  // iframe src; anything else (javascript:, data:, //other-host) is dropped.
  return typeof url === 'string' && /^(https?:\/\/|\/(?![/\\]))/i.test(url) ? url : undefined;
};

/** Filter for facility-gated items: true when the item shows for this facility type. */
export const isVisibleTo = (facilityType) => (item) =>
  !item.showOnlyWith || item.showOnlyWith.includes(facilityType);

/** Where a top tab goes. cat is the category for tabId, if it is visible. */
export const routeForTab = (cat, tabId) => {
  if (!cat || cat.type === 'movies') return `/category/${tabId}`;
  if (cat.link) return `/external/${tabId}`;
  if (cat.type && cat.type !== 'none') return `/${cat.type}/${tabId}`;
  return `/category/${tabId}`;
};

/**
 * Where a content card goes.
 * isHome: the grid shows top-level categories.
 * activeCategoryId: the selected tab's id, if any; categoryId/subcategoryId: route params.
 */
export const routeForContent = ({ item, isHome, categories, activeCategoryId, categoryId, subcategoryId }) => {
  if (isHome) {
    const cat = categories.find(c => c.id === item.id);
    if (cat?.type === 'movies') return `/movies/${item.id}`;
    if (cat?.link) return `/external/${item.id}`;
    return `/category/${item.id}`;
  }

  const catId = activeCategoryId || parseInt(categoryId, 10);
  const activeCat = categories.find(c => c.id === catId);
  if (activeCat?.type === 'movies') return `/movies/${catId}/subcategory/${item.id}`;

  if (item.link) {
    const prefix = item.type || 'external';
    return subcategoryId
      ? `/${prefix}/${categoryId}/subcategory/${subcategoryId}/subsubcategory/${item.id}`
      : `/${prefix}/${catId}/subcategory/${item.id}`;
  }
  return `/category/${catId}/subcategory/${item.id}`;
};

/**
 * Every item a resident can open, flattened for search: linked top-level
 * categories, movie genres, and linked items one or two levels down.
 * Each entry records where it lives (_catId, _subId, _subSubId) and its page type.
 */
// Item types that have their own page; any other linked item opens as an external page.
const PAGE_TYPES = ['archive-audio', 'archive-video'];
const pageType = (item) => (PAGE_TYPES.includes(item.type) ? item.type : 'external');

export const buildSearchIndex = (categories, facilityType) => {
  const visible = isVisibleTo(facilityType);
  const entry = (item, _catId, _subId, _subSubId, _type) =>
    ({ id: item.id, name: item.name, image: item.image, _catId, _subId, _subSubId, _type });
  const items = [];

  categories.forEach(cat => {
    if (cat.link && cat.type !== 'none' && cat.type !== 'movies') {
      items.push(entry(cat, cat.id, null, null, cat.type));
      return;
    }
    if (cat.type === 'movies') {
      cat.subcategories?.forEach(sub => items.push(entry(sub, cat.id, sub.id, null, 'movies')));
      return;
    }
    cat.subcategories?.filter(visible).forEach(sub => {
      if (sub.link) {
        items.push(entry(sub, cat.id, sub.id, null, pageType(sub)));
      } else if (sub.subcategories) {
        sub.subcategories.filter(visible).forEach(subsub => {
          items.push(entry(subsub, cat.id, sub.id, subsub.id, pageType(subsub)));
        });
      }
    });
  });
  return items;
};

/** Where a search result goes: the page for its type, at its place in the menu. */
export const routeForSearchResult = (item) => {
  if (item._type === 'movies') return `/movies/${item._catId}/subcategory/${item._subId}`;
  if (item._subSubId) return `/${item._type}/${item._catId}/subcategory/${item._subId}/subsubcategory/${item._subSubId}`;
  if (item._subId) return `/${item._type}/${item._catId}/subcategory/${item._subId}`;
  // Linked top-level categories only have an /external route.
  return `/external/${item._catId}`;
};
